import { isAxiosError } from 'axios';
import pMap from 'p-map';
import api from '@ilteducation/customer-mgmt/src/api';
import type { CreateUserRequest, MembershipOutput, UserOutput } from '@ilteducation/customer-mgmt-types';
import { isClearSentinel } from './clearSentinel';
import { Role, RoleTranslations, resolveRole } from './roles';

export { isClearSentinel } from './clearSentinel';
export { resolveRole } from './roles';
export type { Role, RoleTranslations } from './roles';

export type MembershipsByUser = Map<string, MembershipOutput[]>;

// Roles the caller is allowed to assign in the organization, as resolved by
// useUserRoles().getRolesPossibleToCreateWith (may also contain 'admin').
export type AllowedRoles = ReadonlyArray<string>;

// Split out from the index signature below so `keyof UserRowFields` stays the literal union of the
// known fields. Keyed off UserRow itself it would widen to `string`, because the index signature
// accepts anything, and a mistyped field name would compile happily.
type UserRowFields = {
  name?: string;
  username?: string;
  type?: string;
  group?: string; // can be multiple with | separator, eg "group1|group2|group3"
  email?: string;
  eppn?: string;
  gsuiteemail?: string;
  office365?: string;
  bankid?: string;
  sms?: string;
  row?: number; // row number of the excel sheet
  status?: string; // created | error | updated | not-imported
};

export type UserRowField = keyof UserRowFields & string;

export type UserRow = UserRowFields & {
  [key: string]: string | number | undefined;
};

export type UserBatch = {
  // Role words, plus the sentinel a cell carries to clear a value.
  translations: RoleTranslations & { clear: string };
  users: UserRow[];
};

export type BatchUserResult = {
  status: 'created' | 'updated' | 'error';
  errors?: unknown;
};

export const isStudentRowWithGroups = (row: UserRow, translations: UserBatch['translations']): boolean =>
  !!row.group && resolveRole(row.type || '', translations) === 'student';

export const splitName = (name: string | undefined): { firstName: string; lastName: string } => {
  const nameParts = (name ?? '').trim().replace(/\s+/g, ' ').split(' ');
  const firstName = nameParts.length === 1 ? nameParts[0] : nameParts.slice(0, 1).join(' ').trim();
  const lastName = nameParts.length === 1 ? '' : nameParts.slice(1).join(' ').trim();
  return { firstName, lastName };
};

// Userville lowercases and re-capitalizes names on the way out, so a name only differs in a way
// worth writing when it differs by more than casing.
const isSameName = (a: string, b: string | undefined): boolean => a.toLowerCase() === (b ?? '').toLowerCase();

// The excel template's columns, in the order they appear in the file. This is the single source of
// truth for the importer: the column-to-field mapping used when reading the sheet, which fields are
// mandatory, and which columns userville stores as UserIdentifier rows. Note the column name and
// the identifier type are not always the same (`office365` is stored as `office365id`).
//
// UserIdentifier's primary key is (realm, value, type), so an identifier value of a given type can
// exist only once per realm.
export const IMPORT_COLUMNS: ReadonlyArray<{
  field: UserRowField;
  labelKey: string;
  mandatory?: boolean;
  identifierType?: string;
}> = [
  { field: 'name', labelKey: 'account.name', mandatory: true },
  { field: 'email', labelKey: 'account.email' },
  { field: 'username', labelKey: 'account.username', mandatory: true },
  { field: 'type', labelKey: 'type', mandatory: true },
  { field: 'group', labelKey: 'group' },
  { field: 'eppn', labelKey: 'account.eppn', identifierType: 'eppn' },
  { field: 'gsuiteemail', labelKey: 'account.gsuite_email', identifierType: 'gsuiteemail' },
  { field: 'office365', labelKey: 'account.office365', identifierType: 'office365id' },
  { field: 'bankid', labelKey: 'account.bankid', identifierType: 'bankid' },
  { field: 'sms', labelKey: 'account.sms', identifierType: 'sms' },
];

// Both lookups below are Maps rather than objects because they are queried with attribute names
// straight out of a backend payload. A plain object inherits from Object.prototype, so looking up
// 'toString' or 'constructor' would return an inherited member instead of nothing.

// Label key per column, so a column added to IMPORT_COLUMNS is titled correctly without also having
// to be added to the label lookup in the component.
export const COLUMN_LABEL_KEYS: ReadonlyMap<string, string> = new Map(
  IMPORT_COLUMNS.map((column) => [column.field, column.labelKey]),
);

// The attribute customer-mgmt reports a conflict against -> the sheet column it came from. It names
// the field of its own request that collided: `userName`, or `identifiers.<type>` using the type
// userville stores the identifier under, which is not always the column name (`office365` is stored
// as `office365id`).
const CONFLICT_ATTRIBUTE_TO_FIELD: ReadonlyMap<string, UserRowField> = new Map<string, UserRowField>([
  ['userName', 'username'],
  ...IMPORT_COLUMNS.filter((column) => !!column.identifierType).map((column): [string, UserRowField] => [
    `identifiers.${column.identifierType}`,
    column.field,
  ]),
]);

export const MANDATORY_FIELDS: ReadonlyArray<UserRowField> = IMPORT_COLUMNS.filter((column) => column.mandatory).map(
  (column) => column.field,
);

export const IDENTIFIER_FIELDS: ReadonlyArray<UserRowField> = IMPORT_COLUMNS.filter(
  (column) => !!column.identifierType,
).map((column) => column.field);

// Works out which column a 409 was about, so a clashing identifier is not reported against the
// username.
//
// customer-mgmt answers with { name: 'ConflictError', errors: [{ attribute, reason }] }, naming the
// field of its request that collided, so in the ordinary case the attribute is simply translated to
// the column it came from.
//
// The reason is always 'taken' rather than the payload's own: that field carries a sentence such as
// "identifier already used", whereas the caller maps short codes, so passing it through would render
// as "unknown".
export const conflictValidationErrors = (
  responseData: unknown,
  user: UserRow,
): Array<{ attribute: string; reason: string }> => {
  // The shape is whatever the gateway happened to return, so it is not safe to assume an array.
  const rawErrors = (responseData as { errors?: unknown } | null | undefined)?.errors;
  const errors: Array<{ attribute?: unknown }> = Array.isArray(rawErrors) ? rawErrors : [];

  const fields = errors
    .map((error) => (typeof error?.attribute === 'string' ? error.attribute : undefined))
    .filter((attribute): attribute is string => !!attribute)
    // A bare 'identifiers' means the type was not established, so it names no column and is left to
    // the fallback below. Anything else unrecognised is kept and reported on the row.
    .map((attribute) => CONFLICT_ATTRIBUTE_TO_FIELD.get(attribute) ?? attribute)
    .filter((attribute) => attribute !== 'identifiers');

  if (fields.length > 0) {
    return [...new Set(fields)].map((attribute) => ({ attribute, reason: 'taken' }));
  }

  // A bare 'identifiers' names no column, which happens when userville hits the constraint but its
  // follow-up query no longer finds the row that collided. The row's own identifiers are the only
  // candidates left, and the username is not one of them: upsert-user answers a username that
  // exists elsewhere by returning and associating that user rather than by conflicting.
  const identifierFields = IDENTIFIER_FIELDS.filter((field) => !!user[field]);

  return identifierFields.length > 0
    ? identifierFields.map((field) => ({ attribute: field, reason: 'taken' }))
    : [{ attribute: 'username', reason: 'taken' }];
};

// `null` where the cell asked for the stored value to be removed.
const identifierEntries = (
  user: UserRow,
  translations?: UserBatch['translations'],
): Array<{ type: string; value: string | null }> => {
  const entries: Array<{ type: string; value: string | null }> = [];
  IMPORT_COLUMNS.forEach(({ field, identifierType }) => {
    const value = user[field];
    if (!identifierType || !value) return;
    entries.push({ type: identifierType, value: isClearSentinel(value, translations) ? null : String(value) });
  });
  return entries;
};

// A create takes strings only, so a clear is dropped - including on a row upsert-user answers with a
// user that already exists elsewhere.
const buildIdentifiers = (
  user: UserRow,
  translations?: UserBatch['translations'],
): Record<string, string> | undefined => {
  const ids: Record<string, string> = {};
  identifierEntries(user, translations).forEach(({ type, value }) => {
    if (value !== null) ids[type] = value;
  });
  return Object.keys(ids).length > 0 ? ids : undefined;
};

const buildIdentifierPatch = (
  user: UserRow,
  translations?: UserBatch['translations'],
): Record<string, string | null> | undefined => {
  const ids: Record<string, string | null> = {};
  identifierEntries(user, translations).forEach(({ type, value }) => {
    ids[type] = value;
  });
  return Object.keys(ids).length > 0 ? ids : undefined;
};

export const useImportUsers = () => {
  const isUserInRowChanged = (
    excelUser: UserRow,
    existingUser: UserOutput,
    organizationUid: string,
    translations: UserBatch['translations'],
  ): boolean => {
    let userUpdated = false;

    // A deactivated user in this organization is reactivated by the patch below, even when every
    // other column in the row already matches.
    if (!existingUser.active) userUpdated = true;

    if (excelUser.name) {
      const { firstName, lastName } = splitName(excelUser.name);
      if (!isSameName(firstName, existingUser.firstName) || !isSameName(lastName, existingUser.lastName))
        userUpdated = true;
    }
    if (excelUser.email && excelUser.email !== existingUser.email) userUpdated = true;

    identifierEntries(excelUser, translations).forEach(({ type, value }) => {
      const stored = existingUser.identifiers?.find((identifier) => identifier.type === type)?.value;
      // A clear changes something only if there is something stored.
      if (value === null ? stored !== undefined : value !== stored) userUpdated = true;
    });

    const excelType = resolveRole(excelUser.type || '', translations);
    const existingType = existingUser.associations.find((a) => a.schoolUnit.uid === organizationUid);
    if (excelType && excelType !== existingType?.role) userUpdated = true;

    return userUpdated;
  };

  const fetchMembershipsByUser = async (userUids: string[], organizationUid: string): Promise<MembershipsByUser> => {
    if (userUids.length === 0) {
      return new Map();
    }

    const request = (pageNumber: number) =>
      api.memberships.get({
        pageSize: 100,
        pageNumber,
        filters: { users: userUids, groupSchoolUnits: [organizationUid] },
      });

    const { data: firstPage } = await request(1);
    const memberships = [...firstPage.data];

    const remaining = (firstPage.meta.totalItems ?? 0) - firstPage.data.length;
    if (remaining > 0) {
      const responses = await Promise.all(
        Array.from({ length: Math.ceil(remaining / 100) }, (_, index) => request(index + 2)),
      );
      responses.forEach((response) => memberships.push(...response.data.data));
    }

    return memberships.reduce<MembershipsByUser>((acc, membership) => {
      const forUser = acc.get(membership.user.uid);
      if (forUser) {
        forUser.push(membership);
      } else {
        acc.set(membership.user.uid, [membership]);
      }
      return acc;
    }, new Map());
  };

  const resolveGroupUids = (groupList: string, groupsByName: Map<string, string>): string[] => {
    const groupNames = groupList.includes('|') ? groupList.split('|').map((g) => g.trim()) : [groupList.trim()];
    return [
      ...new Set(groupNames.map((name) => groupsByName.get(name.toLowerCase())).filter((uid): uid is string => !!uid)),
    ];
  };

  const applyGroupsToUser = async (
    userUid: string,
    groupList: string,
    groupsByName: Map<string, string>,
    role: Role | null,
    currentMemberships: MembershipOutput[],
  ) => {
    const groupUids = resolveGroupUids(groupList, groupsByName);
    if (groupUids.length === 0) {
      return;
    }

    const currentGroupUids = new Set(currentMemberships.map((membership) => membership.group.uid));

    await Promise.all(
      groupUids
        .filter((groupUid) => !currentGroupUids.has(groupUid))
        .map(async (groupUid) => {
          try {
            await api.group.addMember(groupUid, userUid);
          } catch (err) {
            // Ignore if user is already a member (409 Conflict)
            if (!isAxiosError(err) || err.response?.status !== 409) {
              throw err;
            }
          }
        }),
    );

    if (role !== 'student') {
      return;
    }

    // currentMemberships is already scoped to this organization, so groups of other organizations
    // are out of reach here and stay untouched.
    await Promise.all(
      currentMemberships
        .filter((membership) => !groupUids.includes(membership.group.uid))
        .map((membership) => api.user.deleteMembership(userUid, membership.group.uid)),
    );
  };

  const toErrorResult = (error: unknown, user: UserRow): BatchUserResult => {
    if (!isAxiosError(error)) {
      return { status: 'error', errors: error };
    }
    if (error.response?.status === 403) {
      return { status: 'error', errors: { error: 'forbidden' } };
    }
    if (error.response?.status === 409) {
      return {
        status: 'error',
        errors: {
          error: 'validation_error',
          validation_errors: conflictValidationErrors(error.response?.data, user),
        },
      };
    }
    return { status: 'error', errors: error.response?.data };
  };

  // import/upsert-user answers with the pre-existing user when the username is already taken
  // outside this organization, so a row here is not necessarily a new user: the groups are applied
  // in a second pass, once the memberships those users already have here can be fetched in one go.
  const createBatchUsers = async (
    organizationUid: string,
    userBatch: UserBatch,
    groupsByName: Map<string, string>,
    allowedRoles: AllowedRoles,
  ): Promise<{ data: Record<string, BatchUserResult> }> => {
    const results: Record<string, BatchUserResult> = {};
    const rowsWithGroups: { username: string; groupList: string; role: Role; userUid: string; user: UserRow }[] = [];

    await pMap(
      userBatch.users,
      async (user) => {
        const username = user.username?.toLowerCase();
        if (!username) return;

        const role = user.type ? resolveRole(user.type, userBatch.translations) : null;
        if (!role) {
          results[username] = {
            status: 'error',
            errors: { error: 'invalid_input', input_errors: { attribute: 'type', reason: 'invalid' } },
          };
          return;
        }

        if (!allowedRoles.includes(role)) {
          results[username] = { status: 'error', errors: { error: 'role_not_permitted', role } };
          return;
        }

        const { firstName, lastName } = splitName(user.name);

        const createData: CreateUserRequest = {
          firstName,
          lastName,
          userName: user.username,
          email: user.email || undefined,
          identifiers: buildIdentifiers(user, userBatch.translations),
          schoolAssociations: [{ schoolUid: organizationUid, role }],
        };

        try {
          const response = await api.import.upsertUser(createData);

          // 201 is a user that did not exist yet, anything else is a pre-existing one that
          // upsert-user only associated with this organization.
          results[username] = { status: response.status === 201 ? 'created' : 'updated' };

          if (user.group) {
            rowsWithGroups.push({ username, groupList: user.group, role, userUid: response.data.uid, user });
          }
        } catch (error) {
          results[username] = toErrorResult(error, user);
        }
      },
      { concurrency: 10 },
    );

    // Only students have their memberships reconciled against the row, so only their existing ones
    // are worth fetching. A pre-existing user can already be a member of groups here without having
    // shown up as an organization member earlier: a deactivated one is only listed for admins.
    const membershipsByUser = await fetchMembershipsByUser(
      rowsWithGroups.filter((row) => row.role === 'student').map((row) => row.userUid),
      organizationUid,
    );

    await pMap(
      rowsWithGroups,
      async ({ username, groupList, role, userUid, user }) => {
        try {
          await applyGroupsToUser(userUid, groupList, groupsByName, role, membershipsByUser.get(userUid) ?? []);
        } catch (error) {
          results[username] = toErrorResult(error, user);
        }
      },
      { concurrency: 10 },
    );

    return { data: results };
  };

  const updateExistingUsers = async (
    users: UserRow[],
    existingUsers: UserOutput[],
    organizationUid: string,
    groupsByName: Map<string, string>,
    translations: UserBatch['translations'],
    allowedRoles: AllowedRoles,
    membershipsByUser: MembershipsByUser,
  ) => {
    return pMap(users, async (user) => {
      const existingUser = existingUsers.find((u) => u.userName.toLowerCase() === user.username?.toLowerCase());

      if (!existingUser) {
        return {
          ...user,
          status: 'error',
          errors: 'User not found in existing users list',
        };
      }

      const requestedRole = resolveRole(user.type || '', translations);
      const currentRole = existingUser.associations.find((a) => a.schoolUnit.uid === organizationUid)?.role;
      if (requestedRole && requestedRole !== currentRole && !allowedRoles.includes(requestedRole)) {
        return {
          ...user,
          status: 'error',
          errors: { error: 'role_not_permitted', role: requestedRole },
        };
      }

      try {
        if (isUserInRowChanged(user, existingUser, organizationUid, translations)) {
          // A row without a name must not blank the stored one, so both parts are left out
          // together - sending only firstName would leave a stale lastName behind.
          const { firstName, lastName } = splitName(user.name);

          await api.user.patch(existingUser.uid, {
            firstName: user.name ? firstName : undefined,
            lastName: user.name ? lastName : undefined,
            // An empty cell leaves the stored value alone, so it must not be sent: the request
            // rejects '' as shorter than the minimum, failing the whole row.
            email: user.email || undefined,
            identifiers: buildIdentifierPatch(user, translations),
            active: existingUser.active ? undefined : true,
          });

          const excelType = resolveRole(user.type || '', translations);
          const existingType = existingUser.associations.find((a) => a.schoolUnit.uid === organizationUid);
          if (excelType && excelType !== existingType?.role) {
            await api.user.updateAssociation(existingUser.uid, organizationUid, excelType);
          }
        }

        // apply the groups of the row, replacing the existing ones for students
        if (user.group) {
          await applyGroupsToUser(
            existingUser.uid,
            user.group,
            groupsByName,
            requestedRole,
            membershipsByUser.get(existingUser.uid) ?? [],
          );
        }

        return {
          ...user,
          status: 'updated',
          errors: [],
        };
      } catch (error) {
        return { ...user, ...toErrorResult(error, user) };
      }
    });
  };

  const removeUsersFromOrganization = async (organizationUid: string, userUids: string[]) => {
    return pMap(
      userUids,
      async (userUid) => {
        try {
          await api.user.deleteAssociation(userUid, organizationUid);
        } catch (error) {
          console.error(`Failed to remove association for user ${userUid}:`, error);
          throw error;
        }
      },
      { concurrency: 10 },
    );
  };

  return { createBatchUsers, updateExistingUsers, removeUsersFromOrganization, fetchMembershipsByUser };
};
